import { DateString } from '@nextforce/api'
import { makeStyles, useTheme } from '@rneui/themed'
import moment from 'moment-timezone'
import React, { useMemo } from 'react'
import CalendarStrip from 'react-native-calendar-strip'
import { WeekSelector } from './selector'

export type MarkedDates = {
  date: string
  dots: {
    color: string | undefined
  }[]
}[]

interface Props {
  markedDates: MarkedDates
  startDate: DateString
  hasNextWeek: boolean
  hasPreviousWeek: boolean
  fetchingNextWeek: boolean
  fetchingPreviousWeek: boolean
  onDateSelected(date: Date): void
  onPreviousWeek(): void
  onNextWeek(): void
  highlightDate?: string // ISO date string
}

interface CustomDateStyles {
  startDate: moment.Moment
  dateNumberStyle: {
    color: string
    fontWeight: 'normal'
  }
  dateNameStyle: {
    color: string
    fontWeight: 'normal'
  }
  highlightDateNumberStyle: {
    color: string
    fontWeight: 'normal'
  }
  highlightDateNameStyle: {
    color: string
    fontWeight: 'normal'
  }
  dateContainerStyle: {}
}

export const WeekView = ({
  markedDates,
  startDate,
  hasNextWeek,
  hasPreviousWeek,
  fetchingNextWeek: isFetchingNextWeek,
  fetchingPreviousWeek: isFetchingPreviousWeek,
  onDateSelected,
  onPreviousWeek,
  onNextWeek,
  highlightDate,
}: Props) => {
  const { theme } = useTheme()
  const styles = useStyles(markedDates)

  const baseStyles = useMemo(
    () => ({
      default: {
        dateNumberStyle: styles.stripDate,
        dateNameStyle: styles.stripDate,
        highlightDateNumberStyle: styles.stripDate,
        highlightDateNameStyle: styles.stripDate,
        dateContainerStyle: styles.dateContainer,
      },
      current: {
        dateNumberStyle: styles.stripDateCurrent,
        dateNameStyle: styles.stripDateCurrent,
        highlightDateNumberStyle: styles.stripDateCurrent,
        highlightDateNameStyle: styles.stripDateCurrent,
        dateContainerStyle: styles.dateContainer,
      },
      selected: {
        dateNumberStyle: {
          ...styles.stripDate,
          fontWeight: 'normal' as const,
          color: theme.colors.primary,
        },
        dateNameStyle: {
          ...styles.stripDate,
          fontWeight: 'normal' as const,
          color: theme.colors.primary,
        },
        dateContainerStyle: styles.dateContainer,
      },
    }),
    [styles, theme]
  )

  const customDatesStyles = useMemo(() => {
    const customStyles: CustomDateStyles[] = []

    for (let i = 0; i < 7; i++) {
      const date = moment(startDate).add(i, 'days')
      const isCurrent = date.isSame(moment(), 'day')

      const dateStyles = {
        startDate: date,
        ...(isCurrent ? baseStyles.current : baseStyles.default),
      }

      customStyles.push(dateStyles)
    }
    return customStyles
  }, [startDate, baseStyles])

  return (
    <CalendarStrip
      key={theme.colors?.black}
      startingDate={moment(startDate)}
      useIsoWeekday={false}
      onDateSelected={date => onDateSelected(date.toDate())}
      style={styles.strip}
      calendarHeaderStyle={styles.stripHeader}
      markedDates={markedDates}
      selectedDate={highlightDate ? moment(highlightDate) : moment()}
      customDatesStyles={customDatesStyles}
      dateNumberStyle={styles.stripDate}
      dateNameStyle={styles.stripDate}
      highlightDateContainerStyle={styles.highlightedDay}
      leftSelector={
        <WeekSelector
          direction="left"
          disabled={!hasPreviousWeek}
          loading={isFetchingPreviousWeek}
          onPress={onPreviousWeek}
        />
      }
      rightSelector={
        <WeekSelector
          direction="right"
          disabled={!hasNextWeek}
          loading={isFetchingNextWeek}
          onPress={onNextWeek}
        />
      }
      showMonth={false}
    />
  )
}

const useStyles = makeStyles((theme, markedDates: MarkedDates) => ({
  highlightedDay: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.grey0,
  },
  dateContainer: {
    paddingTop: 6,
    paddingBottom: 10,
  },
  strip: {
    height: 60,
    paddingHorizontal: 10,
  },
  stripHeader: {
    color: theme.colors?.black,
  },
  stripDate: {
    color: theme.colors?.black,
    fontWeight: 'normal',
    fontSize: 12,
  },
  stripDateCurrent: {
    color: theme.colors?.primary,
    fontWeight: 'normal',
    fontSize: 12,
  },
  dot: {
    color: theme.colors?.grey3,
  },
}))

export type WeekViewRef = CalendarStrip
